import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import { Wallet as WalletIcon, TrendingUp, Calendar, ArrowDownLeft, Receipt, MapPin, User, Hash, ChevronRight } from 'lucide-react';
import { cn } from '../utils/cn';

// View modes for the transaction list
const VIEW_MODES = { list: 'list', byDriver: 'byDriver' };

export default function Wallet() {
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState(VIEW_MODES.list);
  const limit = 20;

  const { data: walletData, isLoading: walletLoading } = useQuery({
    queryKey: ['fleet-wallet'],
    queryFn: async () => {
      const res = await fleetService.getFleetWallet();
      return res.data?.data ?? res.data;
    },
  });

  const { data: earningsData } = useQuery({
    queryKey: ['fleet-earnings'],
    queryFn: async () => {
      const res = await fleetService.getFleetEarningsSummary();
      return res.data?.data ?? res.data;
    },
  });

  const { data: txData, isLoading: txLoading } = useQuery({
    queryKey: ['fleet-wallet-transactions', page],
    queryFn: async () => {
      const res = await fleetService.getFleetWalletTransactions({ page, limit });
      return res.data;
    },
  });

  const balance = walletData?.balance ?? 0;
  const currency = walletData?.currency ?? 'NOK';
  const earnings = earningsData ?? {};
  const transactions = txData?.data ?? [];
  const pagination = txData?.pagination ?? { page: 1, pages: 1, total: 0 };

  // Group transactions by driver for the "by driver" view
  const groupedByDriver = useMemo(() => {
    const map = new Map();
    transactions.forEach((tx) => {
      const driverId = tx.driver_id ?? 'unknown';
      const driverName = tx.driver
        ? `${tx.driver.first_name ?? ''} ${tx.driver.last_name ?? ''}`.trim()
        : 'Unknown Driver';
      if (!map.has(driverId)) {
        map.set(driverId, { driverId, driverName, transactions: [], total: 0 });
      }
      const group = map.get(driverId);
      group.transactions.push(tx);
      group.total += parseFloat(tx.amount || 0);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [transactions]);

  if (walletLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text dark:text-text-dark">Fleet Wallet</h1>
        <p className="text-text-secondary dark:text-text-secondary-dark mt-1">
          Ride payments are credited here. Distribute to drivers from their wallet pages.
        </p>
      </div>

      {/* Balance card */}
      <div className="bg-gradient-to-br from-primary/15 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl p-6 border border-primary/20">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-primary/20">
            <WalletIcon className="w-8 h-8 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-text-secondary dark:text-text-secondary-dark">Available balance</p>
            <p className="text-3xl font-bold text-text dark:text-text-dark">
              {Number(balance).toFixed(2)} {currency}
            </p>
          </div>
        </div>
      </div>

      {/* Earnings summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard title="Total earnings" value={earnings.totalEarnings ?? 0} icon={TrendingUp} color="primary" />
        <SummaryCard title="Today" value={earnings.todayEarnings ?? 0} icon={Calendar} color="success" />
        <SummaryCard title="This week" value={earnings.weekEarnings ?? 0} icon={Receipt} color="info" />
        <SummaryCard title="This month" value={earnings.monthEarnings ?? 0} icon={WalletIcon} color="warning" />
      </div>

      {/* Transaction list */}
      <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark overflow-hidden">
        {/* Header with view toggle */}
        <div className="px-6 py-4 border-b border-border dark:border-border-dark flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-text dark:text-text-dark">Recent transactions</h2>
            <p className="text-sm text-text-secondary dark:text-text-secondary-dark">
              Ride payments credited to your fleet wallet
            </p>
          </div>
          <div className="flex items-center gap-1 bg-surface-secondary dark:bg-surface-secondary-dark rounded-lg p-1 self-start sm:self-auto">
            <button
              onClick={() => setViewMode(VIEW_MODES.list)}
              className={cn(
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                viewMode === VIEW_MODES.list
                  ? 'bg-white dark:bg-surface-dark text-text dark:text-text-dark shadow-sm'
                  : 'text-text-secondary dark:text-text-secondary-dark hover:text-text dark:hover:text-text-dark'
              )}
            >
              All rides
            </button>
            <button
              onClick={() => setViewMode(VIEW_MODES.byDriver)}
              className={cn(
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                viewMode === VIEW_MODES.byDriver
                  ? 'bg-white dark:bg-surface-dark text-text dark:text-text-dark shadow-sm'
                  : 'text-text-secondary dark:text-text-secondary-dark hover:text-text dark:hover:text-text-dark'
              )}
            >
              By driver
            </button>
          </div>
        </div>

        {txLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-text-secondary dark:text-text-secondary-dark">
            No transactions yet. Completed ride payments will appear here.
          </div>
        ) : viewMode === VIEW_MODES.list ? (
          <div className="divide-y divide-border dark:divide-border-dark">
            {transactions.map((tx) => (
              <RideTransactionRow key={tx.id} tx={tx} currency={currency} />
            ))}
          </div>
        ) : (
          <div className="divide-y divide-border dark:divide-border-dark">
            {groupedByDriver.map((group) => (
              <DriverGroup key={group.driverId} group={group} currency={currency} />
            ))}
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-border dark:border-border-dark">
            <p className="text-sm text-text-secondary dark:text-text-secondary-dark">
              Page {pagination.page} of {pagination.pages} ({pagination.total} total)
            </p>
            <div className="flex gap-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className={cn(
                  'px-3 py-1 rounded-lg text-sm font-medium',
                  pagination.page <= 1
                    ? 'bg-surface-secondary dark:bg-surface-secondary-dark text-text-tertiary cursor-not-allowed'
                    : 'bg-primary text-white hover:opacity-90'
                )}
              >
                Previous
              </button>
              <button
                disabled={pagination.page >= pagination.pages}
                onClick={() => setPage((p) => p + 1)}
                className={cn(
                  'px-3 py-1 rounded-lg text-sm font-medium',
                  pagination.page >= pagination.pages
                    ? 'bg-surface-secondary dark:bg-surface-secondary-dark text-text-tertiary cursor-not-allowed'
                    : 'bg-primary text-white hover:opacity-90'
                )}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Single ride transaction row — shows full driver + ride details.
 */
function RideTransactionRow({ tx, currency }) {
  const driverName = tx.driver
    ? `${tx.driver.first_name ?? ''} ${tx.driver.last_name ?? ''}`.trim()
    : null;
  const pickup = tx.ride?.pickup_address ?? null;
  const dropoff = tx.ride?.destination_address ?? null;
  const rideDate = tx.ride?.completed_at
    ? new Date(tx.ride.completed_at).toLocaleString()
    : tx.created_at
    ? new Date(tx.created_at).toLocaleString()
    : null;

  return (
    <div className="px-6 py-4 hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark/50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        {/* Left: icon + details */}
        <div className="flex items-start gap-4 min-w-0">
          <div className="p-2.5 rounded-xl bg-success/10 shrink-0 mt-0.5">
            <ArrowDownLeft className="w-5 h-5 text-success" />
          </div>
          <div className="min-w-0 space-y-1.5">
            {/* Title */}
            <p className="font-semibold text-text dark:text-text-dark">Ride Payment</p>

            {/* Driver */}
            {driverName && (
              <div className="flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondary-dark shrink-0" />
                <span className="text-sm text-text-secondary dark:text-text-secondary-dark">
                  {driverName}
                </span>
                {tx.driver_id && (
                  <span className="text-xs text-text-tertiary dark:text-text-tertiary-dark font-mono">
                    ({String(tx.driver_id).slice(0, 8)}…)
                  </span>
                )}
              </div>
            )}

            {/* Ride ID */}
            {tx.ride_id && (
              <div className="flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondary-dark shrink-0" />
                <span className="text-xs font-mono text-text-tertiary dark:text-text-tertiary-dark break-all">
                  {tx.ride_id}
                </span>
              </div>
            )}

            {/* Pickup → Dropoff */}
            {(pickup || dropoff) && (
              <div className="flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondary-dark shrink-0 mt-0.5" />
                <span className="text-sm text-text-secondary dark:text-text-secondary-dark break-words">
                  {pickup && <span className="text-text dark:text-text-dark">{pickup}</span>}
                  {pickup && dropoff && (
                    <ChevronRight className="inline w-3.5 h-3.5 mx-0.5 text-text-tertiary dark:text-text-tertiary-dark" />
                  )}
                  {dropoff && <span>{dropoff}</span>}
                </span>
              </div>
            )}

            {/* Date */}
            {rideDate && (
              <p className="text-xs text-text-tertiary dark:text-text-tertiary-dark">{rideDate}</p>
            )}
          </div>
        </div>

        {/* Right: amount */}
        <span className="font-bold text-success shrink-0 text-base">
          +{Number(tx.amount).toFixed(2)} {currency}
        </span>
      </div>
    </div>
  );
}

/**
 * Driver group — shows all rides under a driver, collapsed by default.
 */
function DriverGroup({ group, currency }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div>
      {/* Driver header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-6 py-3 flex items-center justify-between bg-surface-secondary dark:bg-surface-secondary-dark/60 hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <User className="w-4 h-4 text-primary" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-text dark:text-text-dark">{group.driverName}</p>
            <p className="text-xs text-text-secondary dark:text-text-secondary-dark">
              {group.transactions.length} {group.transactions.length === 1 ? 'ride' : 'rides'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-bold text-success">+{group.total.toFixed(2)} NOK</span>
          <ChevronRight
            className={cn(
              'w-4 h-4 text-text-tertiary dark:text-text-tertiary-dark transition-transform',
              expanded && 'rotate-90'
            )}
          />
        </div>
      </button>

      {/* Ride rows under this driver */}
      {expanded && (
        <div className="divide-y divide-border dark:divide-border-dark">
          {group.transactions.map((tx) => (
            <div
              key={tx.id}
              className="pl-14 pr-6 py-3 flex items-start justify-between gap-4 hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark/30 transition-colors"
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="p-1.5 rounded-lg bg-success/10 shrink-0 mt-0.5">
                  <ArrowDownLeft className="w-4 h-4 text-success" />
                </div>
                <div className="min-w-0 space-y-1">
                  {tx.ride_id && (
                    <div className="flex items-center gap-1.5">
                      <Hash className="w-3 h-3 text-text-tertiary dark:text-text-tertiary-dark shrink-0" />
                      <span className="text-xs font-mono text-text-tertiary dark:text-text-tertiary-dark break-all">
                        {tx.ride_id}
                      </span>
                    </div>
                  )}
                  {tx.ride?.pickup_address && tx.ride?.destination_address && (
                    <div className="flex items-start gap-1.5">
                      <MapPin className="w-3 h-3 text-text-secondary dark:text-text-secondary-dark shrink-0 mt-0.5" />
                      <span className="text-xs text-text-secondary dark:text-text-secondary-dark break-words">
                        <span className="text-text dark:text-text-dark">{tx.ride.pickup_address}</span>
                        <ChevronRight className="inline w-3 h-3 mx-0.5 text-text-tertiary dark:text-text-tertiary-dark" />
                        {tx.ride.destination_address}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-text-tertiary dark:text-text-tertiary-dark">
                    {tx.ride?.completed_at
                      ? new Date(tx.ride.completed_at).toLocaleString()
                      : tx.created_at
                      ? new Date(tx.created_at).toLocaleString()
                      : ''}
                  </p>
                </div>
              </div>
              <span className="font-semibold text-success shrink-0 text-sm">
                +{Number(tx.amount).toFixed(2)} NOK
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ title, value, icon: Icon, color }) {
  const colorMap = {
    primary: 'from-primary/10 to-primary/5 border-primary/20',
    success: 'from-success/10 to-success/5 border-success/20',
    info: 'from-info/10 to-info/5 border-info/20',
    warning: 'from-warning/10 to-warning/5 border-warning/20',
  };
  const iconColorMap = {
    primary: 'text-primary bg-primary/20',
    success: 'text-success bg-success/20',
    info: 'text-info bg-info/20',
    warning: 'text-warning bg-warning/20',
  };
  return (
    <div className={cn('rounded-xl border p-4 bg-gradient-to-br', colorMap[color] || colorMap.primary)}>
      <div className={cn('inline-flex p-2 rounded-lg mb-2', iconColorMap[color] || iconColorMap.primary)}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-text dark:text-text-dark">
        {Number(value).toFixed(2)} NOK
      </p>
      <p className="text-sm text-text-secondary dark:text-text-secondary-dark">{title}</p>
    </div>
  );
}
